import {TYPE_ONE_BUDGET_FOR_ALL} from '../business_units/BusinessUnitTypes';
import {BudgetHelper, PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE} from '../../utils/BudgetHelper';
import {BU_PERIOD_PERIODIC, BU_PERIOD_TEMPORARY} from '../../periods';
import {DatetimeHelper} from '../../utils/DatetimeHelper';
import {CLIENT_MODE_NEW, WEEKDAYS, accountKey} from './lineWizardDraft';

/**
 * La creación de la línea, al pasar del paso 3 al 4.
 *
 * Es una cadena de pedidos —cliente, línea, días, objetivos, cuentas— y no un alta atómica: el
 * backend no tiene un endpoint que haga todo junto, y crearlo duplicaba lo que ya hacen estos. Para
 * que un fallo a mitad de camino no deje duplicados, **cada eslabón anota lo que creó en el
 * borrador apenas el backend lo confirma**, y el reintento saltea lo que ya está. Así, si fallan los
 * objetivos, «Reintentar» no crea una segunda línea.
 *
 * `api` y `update` se inyectan para poder testear la cadena sin red. Devuelve `{ok: true}` o
 * `{ok: false, stage, response}`: la pantalla decide el copy por la etapa y el código del error.
 */

export const STAGE_CLIENT = 'CLIENT';
export const STAGE_BUSINESS_UNIT = 'BUSINESS_UNIT';
export const STAGE_SCHEDULE = 'SCHEDULE';
export const STAGE_GOALS = 'GOALS';
export const STAGE_ACCOUNTS = 'ACCOUNTS';

const isOk = response => !!response && response.status === 'OK';

// El mismo default que `BusinessUnitPeriodField` (mensual, desde el día 1). No se importa de ahí para
// no arrastrar su calendario a un módulo que es pura lógica.
export const DEFAULT_PERIOD = {type: BU_PERIOD_PERIODIC, range_definition: {first_day: '1'}};

export function businessUnitPayload(draft, clientId) {
    const line = draft.line;
    const period = line.period || DEFAULT_PERIOD;
    const payload = {
        client_id: clientId,
        name: line.name.trim(),
        currency: line.currency,
        allocated_budget: line.budget,
        period,
        // El armado es SÓLO global (feedback del 29/07): el presupuesto por campaña se carga después.
        type: TYPE_ONE_BUDGET_FOR_ALL,
    };
    if (line.partialGoalsInputMode === PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE) {
        const entries = (line.partialGoalsInputDefinition && line.partialGoalsInputDefinition.entries) || [];
        const {firstDate, lastDate} = periodBounds(period);
        payload.budget_partial_goals = BudgetHelper.deriveCumulativeFromDailyRange(line.budget, firstDate, lastDate, entries);
        payload.budget_partial_goals_input_definition = BudgetHelper.BudgetPartialGoalDailyRangeTranslateToSubmit(entries);
    } else {
        payload.budget_partial_goals = BudgetHelper.BudgetPartialGoalTranslateToSubmit(line.partialGoals || []);
        payload.budget_partial_goals_input_definition = null;
    }
    return payload;
}

function periodBounds(period) {
    if (period.type === BU_PERIOD_TEMPORARY) {
        const range = period.range_definition || {};
        return {
            firstDate: range.from_date ? DatetimeHelper.friendlyDDMMYYYYToDate(range.from_date, '-') : null,
            lastDate: range.to_date ? DatetimeHelper.friendlyDDMMYYYYToDate(range.to_date, '-') : null,
        };
    }
    return DatetimeHelper.calculateDatesForMonthlyPeriod((period.range_definition || {}).first_day || 1);
}

export function scheduleDays(draft) {
    const days = {};
    WEEKDAYS.forEach(day => {
        days[`works_on_${day}`] = !draft.line.schedule.enabled || !!draft.line.schedule.days[day];
    });
    return days;
}

export function goalPayload(goal) {
    return {
        has_goal: true,
        metric_name: goal.metricName,
        value: goal.value,
        metric_conversion_configuration: goal.metricConversionConfiguration !== undefined
            ? goal.metricConversionConfiguration : null,
    };
}

/** El id que devuelve `client/add`: el backend manda el id pelado en `data`, pero se tolera `{id}`. */
function clientIdOf(response) {
    const data = response && response.data;
    if (data && typeof data === 'object') return data.id !== undefined ? String(data.id) : null;
    return data !== undefined && data !== null ? String(data) : null;
}

/**
 * Los ids de `AdAccount` (pk) de las cuentas elegidas, resueltos contra `all_ad_accounts` de la
 * línea recién creada. `get-accounts` habla en ids EXTERNOS y `campaigns-configurations/add` pide
 * pks: este cruce es el puente. Una cuenta que no aparece no se descarta en silencio —quedaría una
 * línea sin la cuenta que el usuario eligió—: se informa.
 */
export function resolveAdAccountIds(allAdAccounts, selected) {
    const byKey = {};
    (allAdAccounts || []).forEach(account => {
        byKey[accountKey({channel: account.channel, account_id: String(account.account_id)})] = account.id;
    });
    const ids = [];
    const missing = [];
    (selected || []).forEach(account => {
        const id = byKey[accountKey(account)];
        if (id === undefined || id === null) missing.push(account);
        else ids.push(String(id));
    });
    return {ids, missing};
}

/**
 * Las cuentas elegidas que la línea todavía no tiene. `configurations` es la respuesta de
 * `business-unit/:id/campaigns-configurations`: un objeto por BUCC con su `ad_account_id` (pk).
 */
export function idsNotYetInLine(ids, configurations) {
    const inLine = new Set(Object.values(configurations || {}).map(config => String(config.ad_account_id)));
    return ids.filter(id => !inLine.has(String(id)));
}

export async function materializeLine({draft, update, api}) {
    let current = draft;
    const save = changes => {
        current = update(previous => ({...previous, created: {...previous.created, ...changes}}));
        return current;
    };

    // 1 · El cliente, si es nuevo.
    let clientId = current.created.clientId || (current.client.mode === CLIENT_MODE_NEW ? null : current.client.id);
    if (!clientId) {
        const response = await api.postClient(current.client.newName.trim());
        if (!isOk(response) || !clientIdOf(response)) return {ok: false, stage: STAGE_CLIENT, response};
        clientId = clientIdOf(response);
        save({clientId});
    }

    // 2 · La línea.
    let businessUnitId = current.created.businessUnitId;
    if (!businessUnitId) {
        const response = await api.postBusinessUnit(businessUnitPayload(current, clientId));
        const id = response && response.data && response.data.business_unit_id;
        if (!isOk(response) || !id) return {ok: false, stage: STAGE_BUSINESS_UNIT, response};
        businessUnitId = String(id);
        save({businessUnitId});
    }

    // 3 · Los días de corrida. Una línea nace corriendo todos los días: sólo se escribe si se eligió.
    if (!current.created.scheduleSaved) {
        if (current.line.schedule.enabled) {
            const response = await api.putScheduleConfiguration(businessUnitId, scheduleDays(current));
            if (!isOk(response)) return {ok: false, stage: STAGE_SCHEDULE, response};
        }
        save({scheduleSaved: true});
    }

    // 4 · Objetivo 1 y 2, por el endpoint parcial.
    for (let index = 0; index < 2; index += 1) {
        if (current.created.goalsSaved[index]) continue;
        const goal = current.line.goals[index];
        if (goal) {
            const response = await api.putGoal(businessUnitId, index + 1, goalPayload(goal));
            if (!isOk(response)) return {ok: false, stage: STAGE_GOALS, response};
        }
        save({goalsSaved: current.created.goalsSaved.map((saved, i) => i === index ? true : saved)});
    }

    // 5 · Las cuentas. `campaigns-configurations/add` vincula al cliente las que todavía no son
    // suyas, así que no hace falta un alta aparte por cuenta.
    if (!current.created.configurationsCreated) {
        const listResponse = await api.fetchBusinessUnitAllAdAccounts(businessUnitId);
        if (!isOk(listResponse)) return {ok: false, stage: STAGE_ACCOUNTS, response: listResponse};
        const {ids, missing} = resolveAdAccountIds(listResponse.data, current.accounts);
        if (missing.length > 0 || ids.length === 0) {
            return {ok: false, stage: STAGE_ACCOUNTS, response: {status: 'ACCOUNTS_NOT_FOUND', error_code: 'ACCOUNTS_NOT_FOUND'},
                    missing};
        }
        // `campaigns-configurations/add` rechaza el pedido ENTERO si alguna cuenta ya está en la
        // línea, y no es atómico: si falla en la cuenta 2, la 1 queda creada. Sin esto, después de un
        // add parcial (o de una respuesta que no llegó) cada «Reintentar» chocaba contra la cuenta ya
        // agregada y el usuario nunca llegaba al paso 4. Se manda sólo lo que falta.
        const existingResponse = await api.fetchCampaignsConfigurations(businessUnitId);
        if (!isOk(existingResponse)) return {ok: false, stage: STAGE_ACCOUNTS, response: existingResponse};
        const pending = idsNotYetInLine(ids, existingResponse.data);
        if (pending.length > 0) {
            const response = await api.postCampaignsConfigurations(businessUnitId, pending);
            if (!isOk(response)) return {ok: false, stage: STAGE_ACCOUNTS, response};
        }
        save({configurationsCreated: true});
    }

    return {ok: true, clientId, businessUnitId};
}
